/**
 * CoachPlaybook - Lesson Library Component
 * Version: 0.7.0
 * Description: Browse pre-built lessons and user plays — tabbed interface with expandable lesson cards
 */

import React, { useState, useMemo } from 'react';
import { getModulesForSport, getDifficultyColor } from '../../utils/learn/lessonModules';

/**
 * Calculate difficulty from step count
 */
const getDifficulty = (play) => {
  const stepCount = (play.steps?.length || 0) + (play.paths?.length || 0);
  if (stepCount <= 3) return { label: 'Beginner', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' };
  if (stepCount <= 6) return { label: 'Intermediate', color: '#fbbf24', bg: 'rgba(251,191,36,0.15)' };
  return { label: 'Advanced', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' };
};

/**
 * Difficulty badge background helper
 */
const getDifficultyBg = (difficulty) => {
  switch (difficulty) {
    case 'beginner': return 'rgba(34,197,94,0.15)';
    case 'intermediate': return 'rgba(245,158,11,0.15)';
    case 'advanced': return 'rgba(239,68,68,0.15)';
    default: return 'rgba(100,116,139,0.15)';
  }
};

const LessonLibrary = ({ plays = [], sports = [], onStartQuiz, currentSport }) => {
  const [activeTab, setActiveTab] = useState('lessons'); // 'lessons' | 'quizzes'
  const [search, setSearch] = useState('');
  const [filterSport, setFilterSport] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedLessons, setExpandedLessons] = useState({});

  // Get lesson modules for the current sport
  const sportId = currentSport || (sports.length > 0 ? sports[0]?.id : 'basketball');
  const lessonModules = useMemo(() => {
    if (filterSport !== 'all') return getModulesForSport(filterSport);
    // Show all sports modules
    const allModules = [];
    const sportIds = ['basketball', 'soccer', 'baseball'];
    for (const id of sportIds) {
      const modules = getModulesForSport(id);
      if (modules.length > 0) {
        allModules.push({ sportId: id, modules });
      }
    }
    return allModules;
  }, [filterSport]);

  const filteredPlays = useMemo(() => {
    let result = [...plays];

    if (search.trim()) {
      const term = search.toLowerCase().trim();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(term) ||
        p.type?.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term) ||
        p.notes?.toLowerCase().includes(term)
      );
    }

    if (filterSport !== 'all') {
      result = result.filter(p => (p.sport || p.sportId || 'basketball') === filterSport);
    }

    if (filterType !== 'all') {
      result = result.filter(p => p.type === filterType);
    }

    if (filterDifficulty !== 'all') {
      result = result.filter(p => getDifficulty(p).label.toLowerCase() === filterDifficulty);
    }

    return result;
  }, [plays, search, filterSport, filterType, filterDifficulty]);

  const groupedPlays = useMemo(() => {
    const groups = {};
    for (const play of filteredPlays) {
      const sid = play.sport || play.sportId || 'basketball';
      if (!groups[sid]) groups[sid] = [];
      groups[sid].push(play);
    }
    return groups;
  }, [filteredPlays]);

  const uniqueTypes = useMemo(() => {
    const types = new Set(plays.map(p => p.type).filter(Boolean));
    return Array.from(types);
  }, [plays]);

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const toggleLesson = (lessonId) => {
    setExpandedLessons(prev => ({ ...prev, [lessonId]: !prev[lessonId] }));
  };

  const sportNames = { basketball: 'Basketball', soccer: 'Soccer', baseball: 'Baseball' };
  const sportIcons = { basketball: '\u{1F3C0}', soccer: '\u26BD', baseball: '\u26BE' };

  const styles = {
    container: {
      height: '100%',
      overflowY: 'auto',
      backgroundColor: '#0f172a',
      color: '#f8fafc',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    inner: {
      padding: '24px',
      maxWidth: '960px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '24px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '900',
      color: '#f1f5f9',
      marginBottom: '4px'
    },
    subtitle: {
      fontSize: '14px',
      color: '#94a3b8'
    },
    tabs: {
      display: 'flex',
      gap: '4px',
      marginBottom: '24px',
      backgroundColor: '#1e293b',
      borderRadius: '12px',
      padding: '4px',
      border: '2px solid #334155'
    },
    tab: (active) => ({
      flex: 1,
      padding: '10px 16px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: active ? '#3b82f6' : 'transparent',
      color: active ? '#fff' : '#94a3b8',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }),
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '10px',
      marginBottom: '24px'
    },
    searchInput: {
      flex: '1 1 200px',
      padding: '10px 14px',
      borderRadius: '10px',
      border: '2px solid #334155',
      backgroundColor: '#1e293b',
      color: '#f1f5f9',
      fontSize: '14px',
      outline: 'none'
    },
    select: {
      padding: '10px 14px',
      borderRadius: '10px',
      border: '2px solid #334155',
      backgroundColor: '#1e293b',
      color: '#f1f5f9',
      fontSize: '13px',
      outline: 'none',
      cursor: 'pointer',
      minWidth: '130px'
    },
    // Lesson module styles
    moduleCard: {
      backgroundColor: '#1e293b',
      borderRadius: '14px',
      border: '2px solid #334155',
      marginBottom: '14px',
      overflow: 'hidden',
      transition: 'border-color 0.2s ease'
    },
    moduleHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '18px',
      cursor: 'pointer',
      userSelect: 'none'
    },
    moduleTitle: {
      fontSize: '16px',
      fontWeight: '800',
      color: '#f1f5f9'
    },
    moduleDescription: {
      fontSize: '13px',
      color: '#94a3b8',
      marginTop: '2px'
    },
    moduleMeta: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    lessonCount: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '600'
    },
    chevron: (expanded) => ({
      fontSize: '18px',
      color: '#64748b',
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease'
    }),
    lessonsList: {
      borderTop: '1px solid #334155',
      padding: '0'
    },
    lessonItem: {
      borderBottom: '1px solid #1e293b',
      cursor: 'pointer'
    },
    lessonHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '14px 18px',
      backgroundColor: '#172033'
    },
    lessonTitle: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#e2e8f0'
    },
    lessonContent: {
      padding: '14px 18px 18px',
      backgroundColor: '#0f1a2e',
      borderTop: '1px solid #253349'
    },
    lessonText: {
      fontSize: '14px',
      color: '#cbd5e1',
      lineHeight: '1.7',
      marginBottom: '14px'
    },
    keyPointsTitle: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#94a3b8',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '8px'
    },
    keyPoint: {
      fontSize: '13px',
      color: '#e2e8f0',
      padding: '6px 0 6px 16px',
      borderLeft: '3px solid #3b82f6',
      marginBottom: '6px',
      lineHeight: '1.4'
    },
    // Quiz card styles (unchanged from original)
    groupHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '14px',
      marginTop: '20px'
    },
    groupTitle: {
      fontSize: '18px',
      fontWeight: '800',
      color: '#e2e8f0'
    },
    groupBadge: {
      fontSize: '12px',
      fontWeight: '700',
      color: '#94a3b8',
      backgroundColor: '#334155',
      padding: '2px 10px',
      borderRadius: '999px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: '14px',
      marginBottom: '16px'
    },
    card: {
      backgroundColor: '#1e293b',
      borderRadius: '14px',
      border: '2px solid #334155',
      padding: '18px',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      transition: 'border-color 0.2s ease, transform 0.15s ease',
      cursor: 'default'
    },
    cardTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start'
    },
    playName: {
      fontSize: '16px',
      fontWeight: '800',
      color: '#f1f5f9',
      lineHeight: '1.3'
    },
    sportIcon: {
      fontSize: '22px',
      flexShrink: 0
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap'
    },
    badge: (color, bg) => ({
      fontSize: '11px',
      fontWeight: '700',
      color,
      backgroundColor: bg,
      padding: '3px 10px',
      borderRadius: '999px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }),
    typeBadge: {
      fontSize: '11px',
      fontWeight: '600',
      color: '#94a3b8',
      backgroundColor: '#0f172a',
      padding: '3px 10px',
      borderRadius: '999px'
    },
    stepCount: {
      fontSize: '12px',
      color: '#64748b',
      fontWeight: '600'
    },
    startButton: {
      marginTop: 'auto',
      padding: '10px 16px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: '#3b82f6',
      color: '#fff',
      fontSize: '14px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      textAlign: 'center'
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      color: '#94a3b8',
      textAlign: 'center'
    },
    sportSection: {
      marginBottom: '28px'
    },
    sportSectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      marginBottom: '14px',
      paddingBottom: '10px',
      borderBottom: '1px solid #334155'
    },
    sportSectionTitle: {
      fontSize: '20px',
      fontWeight: '800',
      color: '#f1f5f9'
    }
  };

  // Render lessons tab content
  const renderLessonsTab = () => {
    // When filtering by a specific sport, lessonModules is a flat array
    // When showing all, it's an array of { sportId, modules }
    const isFiltered = filterSport !== 'all';

    if (isFiltered) {
      const modules = lessonModules;
      if (!modules || modules.length === 0) {
        return (
          <div style={styles.emptyState}>
            <p style={{ fontSize: '48px', marginBottom: '16px' }}>📖</p>
            <p style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>
              No Lessons Available
            </p>
            <p style={{ fontSize: '14px' }}>
              No pre-built lessons for this sport yet. Check back soon!
            </p>
          </div>
        );
      }
      return (
        <div>
          {modules.map(mod => renderModuleCard(mod))}
        </div>
      );
    }

    // Show all sports
    return (
      <div>
        {lessonModules.map(({ sportId: sid, modules }) => (
          <div key={sid} style={styles.sportSection}>
            <div style={styles.sportSectionHeader}>
              <span style={{ fontSize: '26px' }}>{sportIcons[sid] || ''}</span>
              <span style={styles.sportSectionTitle}>{sportNames[sid] || sid}</span>
              <span style={styles.groupBadge}>
                {modules.reduce((acc, m) => acc + m.lessons.length, 0)} lessons
              </span>
            </div>
            {modules.map(mod => renderModuleCard(mod))}
          </div>
        ))}
      </div>
    );
  };

  const renderModuleCard = (mod) => {
    const isExpanded = expandedModules[mod.id];
    const diffColor = getDifficultyColor(mod.difficulty);
    const diffBg = getDifficultyBg(mod.difficulty);

    return (
      <div key={mod.id} style={styles.moduleCard}>
        <div
          style={styles.moduleHeader}
          onClick={() => toggleModule(mod.id)}
        >
          <div>
            <div style={styles.moduleTitle}>{mod.title}</div>
            <div style={styles.moduleDescription}>{mod.description}</div>
          </div>
          <div style={styles.moduleMeta}>
            <span style={styles.badge(diffColor, diffBg)}>
              {mod.difficulty}
            </span>
            <span style={styles.lessonCount}>
              {mod.lessons.length} {mod.lessons.length === 1 ? 'lesson' : 'lessons'}
            </span>
            <span style={styles.chevron(isExpanded)}>&#9660;</span>
          </div>
        </div>
        {isExpanded && (
          <div style={styles.lessonsList}>
            {mod.lessons.map(lesson => {
              const lessonExpanded = expandedLessons[lesson.id];
              return (
                <div key={lesson.id} style={styles.lessonItem}>
                  <div
                    style={styles.lessonHeader}
                    onClick={() => toggleLesson(lesson.id)}
                  >
                    <span style={styles.lessonTitle}>{lesson.title}</span>
                    <span style={styles.chevron(lessonExpanded)}>&#9660;</span>
                  </div>
                  {lessonExpanded && (
                    <div style={styles.lessonContent}>
                      <p style={styles.lessonText}>{lesson.content}</p>
                      {lesson.keyPoints && lesson.keyPoints.length > 0 && (
                        <div>
                          <div style={styles.keyPointsTitle}>Key Points</div>
                          {lesson.keyPoints.map((point, idx) => (
                            <div key={idx} style={styles.keyPoint}>{point}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render quizzes tab content
  const renderQuizzesTab = () => {
    if (filteredPlays.length === 0) {
      return (
        <div style={styles.emptyState}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>📚</p>
          <p style={{ fontSize: '18px', fontWeight: '700', color: '#e2e8f0', marginBottom: '8px' }}>
            {plays.length === 0 ? 'No Plays Available' : 'No Matching Plays'}
          </p>
          <p style={{ fontSize: '14px' }}>
            {plays.length === 0
              ? 'Create some plays in the Playbook first, then come back to quiz yourself.'
              : 'Try adjusting your search or filters.'}
          </p>
        </div>
      );
    }

    return Object.entries(groupedPlays).map(([sid, sportPlays]) => {
      const sportConfig = sports.find(s => s.id === sid) || { name: sid, icon: '' };
      return (
        <div key={sid}>
          <div style={styles.groupHeader}>
            <span style={styles.sportIcon}>{sportConfig.icon || ''}</span>
            <span style={styles.groupTitle}>{sportConfig.name || sid}</span>
            <span style={styles.groupBadge}>{sportPlays.length} {sportPlays.length === 1 ? 'play' : 'plays'}</span>
          </div>
          <div style={styles.grid}>
            {sportPlays.map(play => {
              const difficulty = getDifficulty(play);
              const stepCount = (play.steps?.length || 0) + (play.paths?.length || 0);
              return (
                <div
                  key={play.id}
                  style={styles.card}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <div style={styles.cardTop}>
                    <span style={styles.playName}>{play.name || 'Untitled Play'}</span>
                    <span style={styles.sportIcon}>{sportConfig.icon || ''}</span>
                  </div>
                  <div style={styles.meta}>
                    <span style={styles.badge(difficulty.color, difficulty.bg)}>{difficulty.label}</span>
                    {play.type && (
                      <span style={styles.typeBadge}>{play.type}</span>
                    )}
                    <span style={styles.stepCount}>{stepCount} {stepCount === 1 ? 'step' : 'steps'}</span>
                  </div>
                  <button
                    style={styles.startButton}
                    onClick={() => onStartQuiz && onStartQuiz(play)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2563eb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#3b82f6'; }}
                  >
                    Start Quiz
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Lesson Library</h1>
          <p style={styles.subtitle}>
            {activeTab === 'lessons'
              ? 'Browse structured lessons covering fundamentals, tactics, and strategy.'
              : 'Select a play to quiz yourself on. Test your knowledge of each play\'s movements and actions.'}
          </p>
        </div>

        {/* Tabs */}
        <div style={styles.tabs}>
          <button
            style={styles.tab(activeTab === 'lessons')}
            onClick={() => setActiveTab('lessons')}
          >
            Lessons
          </button>
          <button
            style={styles.tab(activeTab === 'quizzes')}
            onClick={() => setActiveTab('quizzes')}
          >
            Quiz from Plays
          </button>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          {activeTab === 'quizzes' && (
            <input
              type="text"
              placeholder="Search plays..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          )}
          <select
            value={filterSport}
            onChange={(e) => setFilterSport(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Sports</option>
            <option value="basketball">Basketball</option>
            <option value="soccer">Soccer</option>
            <option value="baseball">Baseball</option>
          </select>
          {activeTab === 'quizzes' && (
            <>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Types</option>
                {uniqueTypes.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
              <select
                value={filterDifficulty}
                onChange={(e) => setFilterDifficulty(e.target.value)}
                style={styles.select}
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </>
          )}
        </div>

        {/* Content */}
        {activeTab === 'lessons' ? renderLessonsTab() : renderQuizzesTab()}
      </div>
    </div>
  );
};

export default LessonLibrary;
